import {Link, NavLink, Outlet, useNavigate} from "react-router-dom";
import {useAuth} from "../context/AuthContext";
import Footer from "../components/Footer";

import kosmosLogo from "../assets/kosmos-logo-white.png";
import {useMemo, useState} from "react";
import {Bars3Icon, XMarkIcon} from "@heroicons/react/16/solid";
import {Dialog, DialogPanel} from "@headlessui/react";
import NavDropdown from "@/components/NavDropdown.tsx";

const navClass = ({isActive}: { isActive: boolean }) =>
    `text-white text-lg transition ${isActive ? "text-primary font-bold" : "hover:text-primary"}`;

export default function EditorLayout() {
    const {user, logout} = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/", {replace: true});
    };

    const isAdmin = !!user?.roles?.includes("ADMIN");
    const isEditor = !!user?.roles?.includes("EDITOR");

    const catItems = useMemo(
        () => [
            {to: "/editor/categories", label: "Categories"},
            {to: "/editor/tags", label: "Tags"},
        ],
        []
    );

    const topLinks = useMemo(
        () => [
            {to: "/editor/news", label: "News"},
            {to: "/editor/competitions", label: "Competitions"},
            {to: "/editor/banners", label: "Banners"},
            {to: "/editor/programs", label: "Programs"},
            {to: "/editor/team-members", label: "Team Members"},
        ],
        []
    );

    return (
        <div className="min-h-full">
            <div className="relative bg-gray-800 pb-32">
                <header>
                    <nav aria-label="Global"
                         className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
                        <Link to="/" className="-m-1.5 p-1.5 inline-flex items-center">
                            <span className="sr-only">Kosmos 94.1</span>
                            <img src={kosmosLogo} alt="Kosmos 94.1 Logo" className="h-10 w-auto"/>
                        </Link>

                        {/* Desktop nav */}
                        <div className="hidden lg:flex lg:items-center lg:gap-x-6">

                            <NavDropdown label="Categories & Tags" items={catItems}/>

                            {topLinks.map((l) => (
                                <NavLink key={l.to} to={l.to} className={navClass} end>
                                    {l.label}
                                </NavLink>
                            ))}

                            {user && (
                                <>
                                    {isAdmin && (
                                        <Link
                                            to="/admin/users"
                                            className="rounded-md bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-emerald-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-emerald-600"
                                        >
                                            Admin
                                        </Link>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleLogout}
                                        className="rounded-md bg-gray-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-gray-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:bg-gray-600"
                                    >
                                        Logout
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <div className="flex lg:hidden">
                            <button
                                type="button"
                                onClick={() => setMobileMenuOpen(true)}
                                className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white hover:bg-white/10"
                            >
                                <span className="sr-only">Open main menu</span>
                                <Bars3Icon aria-hidden="true" className="size-6"/>
                            </button>
                        </div>
                    </nav>

                    {/* Mobile menu */}
                    <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
                        <div className="fixed inset-0 z-50 bg-black/40" aria-hidden="true"/>
                        <DialogPanel
                            className="fixed bg-gray-800 inset-y-0 right-0 z-50 w-full overflow-y-auto p-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
                            <div className="flex items-center justify-between">
                                <Link to="/" className="-m-1.5 p-1.5 inline-flex items-center"
                                      onClick={() => setMobileMenuOpen(false)}>
                                    <span className="sr-only">Kosmos 94.1</span>
                                    <img src={kosmosLogo} alt="Kosmos 94.1 Logo" className="h-10 w-auto"/>
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="-m-2.5 rounded-md p-2.5 text-white hover:bg-white/10"
                                >
                                    <span className="sr-only">Close menu</span>
                                    <XMarkIcon aria-hidden="true" className="size-6"/>
                                </button>
                            </div>

                            <div className="mt-6 space-y-2">

                                {catItems.map((item) => (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        className={({isActive}) => `${navClass({isActive})} block py-2`}
                                        onClick={() => setMobileMenuOpen(false)}
                                        end
                                    >
                                        {item.label}
                                    </NavLink>
                                ))}

                                {topLinks.map((l) => (
                                    <NavLink
                                        key={l.to}
                                        to={l.to}
                                        className={({isActive}) => `${navClass({isActive})} block py-2`}
                                        onClick={() => setMobileMenuOpen(false)}
                                        end
                                    >
                                        {l.label}
                                    </NavLink>
                                ))}

                                {/* Role buttons on mobile */}
                                {user && (
                                    <div className="pt-4 border-t border-white/10 space-y-3">
                                        {isAdmin && (
                                            <Link
                                                to="/admin/users"
                                                onClick={() => setMobileMenuOpen(false)}
                                                className="block rounded-md bg-emerald-600 px-3.5 py-2 text-sm font-semibold text-white text-center hover:bg-emerald-500"
                                            >
                                                Admin
                                            </Link>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleLogout();
                                                setMobileMenuOpen(false);
                                            }}
                                            className="block w-full rounded-md bg-white/10 px-3.5 py-2 text-sm font-semibold text-white text-center hover:bg-white/20"
                                        >
                                            Logout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </DialogPanel>
                    </Dialog>
                </header>

                <header className="py-10">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <h1 className="text-3xl font-bold tracking-tight text-white">Editor Dashboard</h1>
                        <div className="mt-4">
                            {user && (
                                <p className="text-lg text-gray-300">
                                    Welcome, <span className="font-medium">{user.username}</span>
                                </p>
                            )}
                        </div>
                    </div>
                </header>
            </div>

            <main className="relative -mt-32">
                <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
                    <div className="rounded-lg bg-white px-5 py-6 shadow-sm sm:px-6"><Outlet/></div>
                </div>
            </main>

            <Footer/>
        </div>
    );
}
